import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { environment } from '../../../environments/environment';
import {
  buildCopilotAdminConsentUrl,
  COPILOT_CONSENT_AUTO_RETRIED_VALUE,
  COPILOT_CONSENT_AUTO_RETRY_DELAY_MS,
  COPILOT_CONSENT_AUTO_RETRY_STORAGE_PREFIX,
  COPILOT_CONSENT_GRANTED_VALUE,
  COPILOT_CONSENT_SP_PRESENT_CODE,
  COPILOT_CONSENT_SP_PRESENT_MARKER,
  CopilotConsentParam,
} from '../../models/copilot/copilot-consent.enum';

@Component({
  selector: 'app-copilot-consent-complete',
  templateUrl: './copilot-consent-complete.component.html',
  styleUrls: ['./copilot-consent-complete.component.css'],
  standalone: false,
})
export class CopilotConsentCompleteComponent implements OnInit, OnDestroy {
  granted = false;
  errorMessage = '';
  errorCode = '';
  retryable = false;
  retryUrl = '';
  autoRetrying = false;

  private retryTenantId = '';
  private autoRetryTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(private route: ActivatedRoute) {}

  ngOnInit(): void {
    const params = this.route?.snapshot?.queryParamMap;
    if (!params) {
      this.errorMessage = 'This page was opened without a response from Microsoft.';
      return;
    }

    const error = params.get(CopilotConsentParam.ERROR);
    if (error) {
      const description = params.get(CopilotConsentParam.ERROR_DESCRIPTION);
      this.errorMessage = description?.trim() || error;
      this.errorCode = this.extractAadstsCode(this.errorMessage);
      this.retryable = this.isServicePrincipalPresentError(this.errorMessage, this.errorCode);
      if (this.retryable) {
        this.retryUrl = this.buildRetryUrl(params.get(CopilotConsentParam.STATE), this.errorMessage);
        this.maybeAutoRetry();
      }
      return;
    }

    const adminConsent = params.get(CopilotConsentParam.ADMIN_CONSENT);
    this.granted = adminConsent?.toLowerCase() === COPILOT_CONSENT_GRANTED_VALUE;
    if (!this.granted) {
      this.errorMessage = 'Microsoft did not confirm that approval was granted.';
    }
  }

  ngOnDestroy(): void {
    if (this.autoRetryTimeoutId !== null) {
      clearTimeout(this.autoRetryTimeoutId);
    }
  }

  private extractAadstsCode(message: string): string {
    const match = /AADSTS\d+/.exec(message ?? '');
    return match ? match[0] : '';
  }

  private isServicePrincipalPresentError(message: string, code: string): boolean {
    return (
      (code ?? '').toLowerCase() === COPILOT_CONSENT_SP_PRESENT_CODE.toLowerCase() ||
      (message ?? '').toLowerCase().includes(COPILOT_CONSENT_SP_PRESENT_MARKER)
    );
  }

  private buildRetryUrl(state: string | null, message: string): string {
    this.retryTenantId = state?.trim() || this.extractTenantId(message);
    return buildCopilotAdminConsentUrl(window.location.origin, this.retryTenantId, environment?.copilotMsal?.appId ?? '');
  }

  private extractTenantId(message: string): string {
    const match = /present for the tenant\s+([0-9a-fA-F-]{36})/.exec(message ?? '');
    return match ? match[1] : '';
  }

  /**
   * The SP-present error clears with time, not with a different request - so retry it ourselves
   * once, after a short pause, instead of making the admin notice the failure and act on it.
   * Bounded to one attempt per tenant per browser tab: if Microsoft still isn't ready, fall back
   * to the manual "Try approval again" path rather than bouncing the admin in a loop.
   */
  private maybeAutoRetry(): void {
    if (!this.retryUrl || !this.retryTenantId || this.hasAlreadyAutoRetried()) {
      return;
    }
    this.markAutoRetried();
    this.autoRetrying = true;
    this.autoRetryTimeoutId = setTimeout(() => this.navigateTo(this.retryUrl), COPILOT_CONSENT_AUTO_RETRY_DELAY_MS);
  }

  private hasAlreadyAutoRetried(): boolean {
    try {
      return sessionStorage.getItem(this.autoRetryStorageKey()) === COPILOT_CONSENT_AUTO_RETRIED_VALUE;
    } catch {
      return false;
    }
  }

  private markAutoRetried(): void {
    try {
      sessionStorage.setItem(this.autoRetryStorageKey(), COPILOT_CONSENT_AUTO_RETRIED_VALUE);
    } catch {
      // Storage unavailable (private browsing, etc.) - worst case we auto-retry again, still bounded by Microsoft's own outcome.
    }
  }

  private autoRetryStorageKey(): string {
    return `${COPILOT_CONSENT_AUTO_RETRY_STORAGE_PREFIX}${this.retryTenantId}`;
  }

  protected navigateTo(url: string): void {
    window.location.href = url;
  }
}
